import { useState } from 'react';
import { ScrollView } from 'react-native';
import {
    BadgeLabel,
    BodyMediumRegular,
    DownloadIcon,
    isWeb,
    PdfSheet,
    Row,
    RowList,
    Spinner,
    useToastController,
    YStack,
} from 'smoothie';

import { isEmpty } from '../../../../common/misc';
import { useAuthedUser } from '../../../../common/providers/AuthedUserProvider';
import { captureException } from '../../../../logger/Logger';
import { downloadTaxDocument } from '../../../api/StatementApi';
import type { TaxDocument } from '../../../types/TaxDocument';
import { TaxDocumentDisclosureFootnote } from '../../Disclosures';

interface TaxDocumentsProps {
    taxDocuments: TaxDocument[];
    userId?: string;
    downloadDocument?: (userId: string, year: string) => Promise<string>;
}

const TaxDocuments = ({
    taxDocuments,
    userId,
    downloadDocument = downloadTaxDocument,
}: TaxDocumentsProps) => {
    const user = useAuthedUser();
    const documentUserId = userId ?? user.id;

    const [fileInfo, setFileInfo] = useState<{
        uri: string;
        name: string;
    } | null>(null);
    const [isPdfSheetOpen, setIsPdfSheetOpen] = useState(false);

    const onDownloadTaxDocument = async (name: string, year: string) => {
        const uri = await downloadDocument(documentUserId, year);
        setFileInfo({ uri, name });
        setIsPdfSheetOpen(true);
    };

    return (
        <ScrollView style={{ flex: 1 }} showsVerticalScrollIndicator={isWeb}>
            <YStack marginHorizontal={8}>
                {isEmpty(taxDocuments) ? (
                    <BodyMediumRegular>
                        You don&apos;t have any tax documents.
                    </BodyMediumRegular>
                ) : (
                    <YStack>
                        <RowList>
                            {taxDocuments.map((doc) => (
                                <TaxDocumentRow
                                    key={doc.documentId}
                                    taxDocument={doc}
                                    onPressDownload={onDownloadTaxDocument}
                                />
                            ))}
                        </RowList>
                        <TaxDocumentDisclosureFootnote marginTop={16} />
                    </YStack>
                )}
            </YStack>
            {fileInfo?.uri != null && (
                <PdfSheet
                    fileUri={fileInfo.uri}
                    fileName={fileInfo.name}
                    isOpen={isPdfSheetOpen}
                    onClose={() => {
                        setIsPdfSheetOpen(false);
                    }}
                />
            )}
        </ScrollView>
    );
};

interface TaxDocumentRowProps {
    taxDocument: TaxDocument;
    onPressDownload: (name: string, year: string) => Promise<void>;
}

const TaxDocumentRow = ({
    taxDocument,
    onPressDownload,
}: TaxDocumentRowProps) => {
    const toast = useToastController();

    const { name, year } = taxDocument;
    const [isDownloading, setIsDownloading] = useState(false);

    const onPress = async () => {
        setIsDownloading(true);
        try {
            await onPressDownload(name, year);
        } catch (e) {
            captureException(e);
            toast.show('Something went wrong');
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <Row
            title={name}
            onPress={onPress}
            rightContent={
                isDownloading ? (
                    <Spinner />
                ) : (
                    <BadgeLabel
                        variant="neutral"
                        text="Download"
                        icon={DownloadIcon}
                    />
                )
            }
        />
    );
};

export default TaxDocuments;
